import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const AvailabilityChecker = ({ 
  selectedDate, 
  selectedTime, 
  duration, 
  appointments = [],
  currentBookingId = null 
}) => {
  const conflicts = useMemo(() => {
    if (!selectedDate || !selectedTime || !duration) {
      return [];
    }

    const selectedStart = new Date(`${selectedDate}T${selectedTime}`);
    const selectedEnd = new Date(selectedStart.getTime() + parseInt(duration) * 60000);

    return appointments?.filter(apt => {
      if (currentBookingId && apt?.id === currentBookingId) {
        return false;
      }

      if (apt?.date !== selectedDate) {
        return false;
      }

      const aptStart = new Date(`${apt.date}T${apt.time}`);
      const aptEnd = new Date(aptStart.getTime() + parseInt(apt.duration) * 60000);

      return (
        (selectedStart >= aptStart && selectedStart < aptEnd) ||
        (selectedEnd > aptStart && selectedEnd <= aptEnd) ||
        (selectedStart <= aptStart && selectedEnd >= aptEnd)
      );
    });
  }, [selectedDate, selectedTime, duration, appointments, currentBookingId]);

  const suggestions = useMemo(() => {
    if (conflicts?.length === 0 || !selectedDate || !selectedTime) {
      return [];
    }

    const allSlots = [];
    for (let hour = 8; hour < 18; hour++) {
      allSlots?.push(`${hour?.toString()?.padStart(2, '0')}:00`);
      allSlots?.push(`${hour?.toString()?.padStart(2, '0')}:30`);
    }

    const availableSlots = allSlots?.filter(time => {
      const slotStart = new Date(`${selectedDate}T${time}`);
      const slotEnd = new Date(slotStart.getTime() + parseInt(duration || 30) * 60000);

      const hasConflict = appointments?.some(apt => {
        if (currentBookingId && apt?.id === currentBookingId) {
          return false;
        }
        if (apt?.date !== selectedDate) {
          return false;
        }

        const aptStart = new Date(`${apt.date}T${apt.time}`);
        const aptEnd = new Date(aptStart.getTime() + parseInt(apt.duration) * 60000);

        return (
          (slotStart >= aptStart && slotStart < aptEnd) ||
          (slotEnd > aptStart && slotEnd <= aptEnd) ||
          (slotStart <= aptStart && slotEnd >= aptEnd)
        );
      });

      return !hasConflict;
    });

    return availableSlots?.slice(0, 3);
  }, [conflicts, selectedDate, selectedTime, duration, appointments, currentBookingId]);

  if (!selectedDate || !selectedTime) {
    return null;
  }

  if (conflicts?.length === 0) {
    return (
      <div className="p-4 bg-success/10 border border-success/20 rounded-lg md:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="CheckCircle2" size={20} className="text-success" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-heading font-semibold text-success mb-1 md:text-base">
              Time Slot Available
            </h4>
            <p className="text-xs text-success/80 md:text-sm">
              This time slot is available for booking. No conflicts detected.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg md:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-heading font-semibold text-warning mb-1 md:text-base">
              Scheduling Conflict Detected
            </h4>
            <p className="text-xs text-warning/80 mb-3 md:text-sm">
              {conflicts?.length} existing {conflicts?.length === 1 ? 'appointment' : 'appointments'} overlap with this time slot
            </p>
            <div className="space-y-2">
              {conflicts?.map((apt) => (
                <div
                  key={apt?.id}
                  className="p-3 bg-background/50 border border-warning/20 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-sm font-medium text-foreground">
                      {apt?.customerName}
                    </p>
                    <span className="px-2 py-1 bg-warning/10 text-warning text-xs font-caption font-medium rounded whitespace-nowrap">
                      {apt?.duration} min
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span className="font-data">{apt?.time}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Briefcase" size={12} />
                      <span>{apt?.service}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {suggestions?.length > 0 && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg md:p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Lightbulb" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-heading font-semibold text-primary mb-1 md:text-base">
                Alternative Time Slots
              </h4>
              <p className="text-xs text-primary/80 mb-3 md:text-sm">
                Available slots on the same day
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions?.map((time) => (
                  <div
                    key={time}
                    className="px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg text-sm font-data font-medium text-primary"
                  >
                    {time}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityChecker;